import React, { useEffect, useState } from "react";
import { X, FileText, Settings, Plus } from "lucide-react";
import classes from "./CreateRequisitionModal.module.css";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../../Components/API/Api";
const CreateRequisitionModal = ({ isOpen, onClose, refreshList }) => {
  const [materials, setMaterials] = useState([
    { material: "", materialId: "", quantity: "", uom: "", remarks: "", availableQty: 0 },
  ]);

  const [requisitionNo, setRequisitionNo] = useState("");
  const [loadingRequisitionNo, setLoadingRequisitionNo] = useState(false);
  const [requisitionDate, setRequisitionDate] = useState("");
  const [department, setDepartment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [productionOrders, setProductionOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedProductionOrder, setSelectedProductionOrder] = useState("");
  const [requestedById, setRequestedById] = useState("");
  const [requestedByName, setRequestedByName] = useState("");

  // New state for raw materials dropdown
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loadingRawMaterials, setLoadingRawMaterials] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRequisitionNo("");
      generateRequisitionNo();
      fetchProductionOrders();
      fetchRawMaterials();
    }
  }, [isOpen]);

  const generateRequisitionNo = async () => {
    try {
      setLoadingRequisitionNo(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(
        `${BASE_URL}/v1/material_requisition/generate_request_number`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setRequisitionNo(response.data?.data || "");
    } catch {
      setRequisitionNo("");
    } finally {
      setLoadingRequisitionNo(false);
    }
  };

  const fetchProductionOrders = async () => {
    try {
      setLoadingOrders(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${BASE_URL}/v1/production_order/planned`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (Array.isArray(res.data?.data)) {
        setProductionOrders(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      setLoadingRawMaterials(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${BASE_URL}/v1/product/raw_materials`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let materialsData = [];

      if (Array.isArray(res.data?.data)) {
        // If data is directly an array
        materialsData = res.data.data;
      } else if (res.data?.data?.items && Array.isArray(res.data.data.items)) {
        // If data has nested items array
        materialsData = res.data.data.items;
      } else if (Array.isArray(res.data)) {
        // If response itself is an array
        materialsData = res.data;
      }

      setRawMaterials(materialsData);
    } catch (err) {
      console.error("Error fetching raw materials:", err);
      Swal.fire("Error", "Failed to load raw materials. Please try again.", "error");
    } finally {
      setLoadingRawMaterials(false);
    }
  };

  const addMaterialRow = () => {
    setMaterials([
      ...materials,
      { material: "", materialId: "", quantity: "", uom: "", remarks: "", availableQty: 0 },
    ]);
  };

  const removeMaterialRow = (index) => {
    if (materials.length > 1) {
      const updatedMaterials = [...materials];
      updatedMaterials.splice(index, 1);
      setMaterials(updatedMaterials);
    }
  };

  const handleMaterialSelect = (index, selectedValue) => {
    const updatedMaterials = [...materials];

    if (selectedValue) {
      const selectedMaterial = rawMaterials.find(
        (item) => item.id === parseInt(selectedValue) ||
          item.product_id === parseInt(selectedValue) ||
          item._id === selectedValue ||
          String(item.id) === String(selectedValue) ||
          String(item.product_id) === String(selectedValue)
      );

      if (selectedMaterial) {
        // Set both material name and ID based on API response structure
        updatedMaterials[index].material = selectedMaterial.name ||
          selectedMaterial.product_name ||
          selectedMaterial.material_name ||
          "";

        // Ensure materialId is a string for consistency
        updatedMaterials[index].materialId = String(
          selectedMaterial.id ||
          selectedMaterial.product_id ||
          selectedMaterial._id ||
          ""
        );

        // Auto-fill UOM if available in the material data
        updatedMaterials[index].uom = selectedMaterial.unit_of_measurement ||
          selectedMaterial.uom ||
          selectedMaterial.unit ||
          "";

        // Get available quantity - check different possible field names
        updatedMaterials[index].availableQty =
          selectedMaterial.available_quantity ||
          selectedMaterial.quantity_available ||
          selectedMaterial.available_qty ||
          selectedMaterial.stock_quantity ||
          selectedMaterial.quantity ||
          selectedMaterial.qty_available ||
          0;
      }
    } else {
      updatedMaterials[index].material = "";
      updatedMaterials[index].materialId = "";
      updatedMaterials[index].availableQty = 0;
    }

    setMaterials(updatedMaterials);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loadingRequisitionNo) {
      Swal.fire("Please wait", "Generating requisition number…", "info");
      return;
    }

    if (
      !requisitionNo ||
      !requisitionDate ||
      !Number(selectedProductionOrder) ||
      !Number(requestedById)
    ) {
      Swal.fire("Warning", "Please fill in all required fields.", "warning");
      return;
    }

    // ✅ STRONG MATERIAL VALIDATION - Fixed the .trim() error
    // ✅ STRONG MATERIAL VALIDATION - Simplified version
    const validMaterials = materials.filter(
      (m) => {
        // Check if materialId exists (could be string or number)
        const hasValidMaterialId = m.materialId &&
          (typeof m.materialId === 'string' && m.materialId.trim() !== '') ||
          (typeof m.materialId === 'number');

        return hasValidMaterialId &&
          m.quantity !== "" &&
          Number(m.quantity) > 0;
      }
    );

    if (validMaterials.length === 0) {
      Swal.fire("Warning", "Please add at least one valid material.", "warning");
      return;
    }

    const exceededMaterials = validMaterials.filter(m =>
      Number(m.quantity) > (m.availableQty || 0)
    );

    if (exceededMaterials.length > 0) {
      Swal.fire({
        title: "Quantity Exceeds Available Stock",
        html: exceededMaterials.map(m =>
          `<b>${m.material}</b>: Requested ${m.quantity} ${m.uom}, Available ${m.availableQty || 0} ${m.uom}`
        ).join('<br/>'),
        icon: "warning",
        confirmButtonText: "Cancel",
      }).then((result) => {
        if (!result.isConfirmed) {
          return; // Don't submit if user cancels
        } else {
          submitRequisition(validMaterials);
        }
      });
    } else {
      submitRequisition(validMaterials);
    }
  };

  const submitRequisition = async (validMaterials) => {
    const token = localStorage.getItem("authToken");

    const payload = {
      requisition_number: requisitionNo,
      production_order_id: Number(selectedProductionOrder),
      requisition_date: requisitionDate,
      requested_by: Number(requestedById),
  

      material: validMaterials.map(m => m.materialId),
      quantity_required: validMaterials.map(m => Number(m.quantity)),
      unit_of_measurement: validMaterials.map(m => m.uom || "unit"),
      remarks: validMaterials.map(m => m.remarks || "Requested for production use"),

    };

    try {
      setSubmitting(true);

      const res = await axios.post(
        `${BASE_URL}/v1/material_requisition/create`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // In the submitRequisition function inside CreateRequisitionModal.js:
      if (res.data?.status) {
        Swal.fire("Success", "Requisition submitted successfully!", "success")
          .then(() => {
            // Reset form
            setMaterials([{ material: "", materialId: "", quantity: "", uom: "", remarks: "", availableQty: 0 }]);
            setSelectedProductionOrder("");
            setRequestedById("");
            setRequestedByName("");
            setRequisitionDate("");
            setDepartment("");

            // Close modal
            onClose();

            if (refreshList && typeof refreshList === 'function') {
              refreshList();
            }
          });
      } else {
        showBackendError(res.data?.message);
      }
    } catch (error) {
      showBackendError(error.response?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const showBackendError = (message) => {
    if (typeof message === "string") {
      Swal.fire("Error", message, "error");
    } else if (typeof message === "object") {
      Swal.fire({
        title: "Validation Error",
        html: Object.values(message).flat().join("<br/>"),
        icon: "error",
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <div className={classes.title}>
            <FileText className={classes.icon} />
            <h2>Raw Materials Requisition Form</h2>
          </div>
          <X className={classes.closeIcon} onClick={onClose} />
        </div>

        <p className={classes.subtitle}>
          Used by production department to request materials from the store.
        </p>

        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.row}>
            <div className={classes.field}>
              <label>Requisition No</label>
              <input
                type="text"
                value={loadingRequisitionNo ? "Generating..." : requisitionNo}
                readOnly
              />
            </div>

            <div className={classes.field}>
              <label>Production Order No</label>
              <select
                value={selectedProductionOrder}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  setSelectedProductionOrder(selectedId);

                  const selectedPO = productionOrders.find(
                    (po) => po.id === parseInt(selectedId)
                  );
                  if (selectedPO) {
                    setRequestedByName(selectedPO.created_by?.name || "");
                    setRequestedById(selectedPO.created_by?.id || "");
                  } else {
                    setRequestedByName("");
                    setRequestedById("");
                  }
                }}
              >
                <option value="">
                  {loadingOrders ? "Loading production orders..." : "-- Select Production Order --"}
                </option>
                {!loadingOrders &&
                  productionOrders.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.production_number}
                    </option>
                  ))}
              </select>
            </div>

            <div className={classes.field}>
              <label>Requisition Date</label>
              <input
                type="date"
                value={requisitionDate}
                onChange={(e) => setRequisitionDate(e.target.value)}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.field}>
              <label>Requested By (Department)</label>
              <input
                type="text"
                placeholder="Enter department name"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
            </div>
            <div className={classes.field}>
              <label>Requested By (Name)</label>
              <input type="text" placeholder="Requester Name" value={requestedByName} readOnly />
            </div>
          </div>

          {/* Materials Section */}
          <div className={classes.sectionHeader}>
            <Settings className={classes.sectionIcon} />
            <h3>Materials Requested</h3>
          </div>

          <table className={classes.table}>
            <thead>
              <tr>
                <th>Material</th>
                <th>UM</th>
                <th>Quantity Required</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={row.materialId}
                      onChange={(e) => handleMaterialSelect(index, e.target.value)}
                      className={classes.materialSelect}
                    >
                      <option value="">-- Select Material --</option>
                      {loadingRawMaterials ? (
                        <option value="" disabled>Loading materials...</option>
                      ) : (
                        rawMaterials.map((material) => {
                          const materialId = material.id || material.product_id || material._id;
                          const materialName = material.name || material.product_name || material.material_name || "Unnamed Material";
                          const materialCode = material.code || material.product_code || "";

                          return (
                            <option key={materialId} value={materialId}>
                              {materialCode ? `${materialCode} - ${materialName}` : materialName}
                            </option>
                          );
                        })
                      )}
                    </select>
                  </td>

                  <td>
                    <input
                      type="text"
                      placeholder="UOM"
                      value={row.uom}
                      onChange={(e) => {
                        const updated = [...materials];
                        updated[index].uom = e.target.value;
                        setMaterials(updated);
                      }}
                    />
                  </td>
                  <td>
                    <div className={classes.quantityCell}>
                      <input
                        type="number"
                        placeholder="Qty"
                        min="0"
                        step="0.01"
                        value={row.quantity}
                        onChange={(e) => {
                          const updated = [...materials];
                          updated[index].quantity = e.target.value;
                          setMaterials(updated);
                        }}
                        className={row.availableQty > 0 && Number(row.quantity) > row.availableQty ? classes.exceedsQty : ""}
                      />
                      {row.materialId && row.availableQty !== undefined && (
                        <div className={classes.availableNote}>
                          Available: <span className={row.availableQty > 0 ? classes.availableText : classes.outOfStockText}>
                            {row.availableQty} {row.uom || ""}
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      placeholder="Remarks"
                      value={row.remarks}
                      onChange={(e) => {
                        const updated = [...materials];
                        updated[index].remarks = e.target.value;
                        setMaterials(updated);
                      }}
                    />
                  </td>
                  <td className={classes.actionCell}>
                    <div className={classes.actionButtons}>
                      <button
                        type="button"
                        className={classes.addBtn}
                        onClick={addMaterialRow}
                        title="Add another material"
                      >
                        <Plus size={18} />
                      </button>
                      {materials.length > 1 && (
                        <button
                          type="button"
                          className={classes.removeBtn}
                          onClick={() => removeMaterialRow(index)}
                          title="Remove this material"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={classes.submitSection}>
            <button type="submit" className={classes.submitBtn} disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Requisition"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRequisitionModal;